<template>
  <b-card class="account-deletion">
    <b-card-title class="text-danger">
      Eliminar mi cuenta
    </b-card-title>
    <p class="text-muted mb-1">
      Borra tus datos personales y cierra tu acceso a YoConstruyo. Tus cotizaciones, pagos y facturas
      se conservan el tiempo que exige la ley fiscal, sin tus datos de contacto.
    </p>
    <b-button
      variant="outline-danger"
      :disabled="loadingPreview"
      @click="openConfirmation"
    >
      <b-spinner
        v-if="loadingPreview"
        small
        class="mr-50"
      />
      Eliminar mi cuenta
    </b-button>

    <b-modal
      v-model="confirmOpen"
      centered
      title="¿Eliminar tu cuenta?"
      no-close-on-backdrop
      @hidden="resetConfirmation"
    >
      <template v-if="preview">
        <p v-if="preview.scope === 'account'">
          Eres el único usuario de <strong>{{ preview.customer_name }}</strong>, así que se elimina la cuenta
          completa: nadie podrá volver a entrar y, si tienes PRO vigente, el tiempo restante se pierde.
        </p>
        <p v-else>
          Se elimina solo tu acceso. <strong>{{ preview.customer_name }}</strong> y
          {{ preview.other_members === 1 ? 'el otro usuario' : `los otros ${preview.other_members} usuarios` }}
          siguen igual.
        </p>

        <b-alert
          :show="Boolean(preview.blocked_reason)"
          variant="warning"
        >
          <div class="alert-body">
            {{ preview.blocked_reason }}
          </div>
        </b-alert>

        <b-form-group
          v-if="!preview.blocked_reason"
          :label="`Para confirmar, escribe tu correo: ${email}`"
          label-for="account-deletion-email"
        >
          <b-form-input
            id="account-deletion-email"
            v-model="typedEmail"
            autocomplete="off"
            :placeholder="email"
          />
        </b-form-group>

        <p
          v-if="errorMessage"
          class="text-danger mb-0"
        >
          {{ errorMessage }}
        </p>
      </template>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="deleting"
          @click="confirmOpen = false"
        >
          Cancelar
        </b-button>
        <b-button
          variant="danger"
          :disabled="!canConfirm || deleting"
          @click="confirmDeletion"
        >
          <b-spinner
            v-if="deleting"
            small
            class="mr-50"
          />
          Eliminar mi cuenta
        </b-button>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BCardTitle, BButton, BModal, BAlert, BFormGroup, BFormInput, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useJwt from '@/auth/jwt/useJwt'

export default {
  components: {
    BCard,
    BCardTitle,
    BButton,
    BModal,
    BAlert,
    BFormGroup,
    BFormInput,
    BSpinner,
  },

  data() {
    return {
      preview: null,
      loadingPreview: false,
      confirmOpen: false,
      typedEmail: '',
      deleting: false,
      errorMessage: '',
    }
  },

  computed: {
    currentUser() {
      return this.$store.getters['auth/currentUser'] || {}
    },
    email() {
      return String(this.currentUser.email || '').trim().toLowerCase()
    },
    canConfirm() {
      return Boolean(this.preview)
        && !this.preview.blocked_reason
        && this.typedEmail.trim().toLowerCase() === this.email
    },
  },

  methods: {
    async openConfirmation() {
      this.loadingPreview = true
      this.errorMessage = ''
      try {
        this.preview = await this.$store.dispatch('users/fetchAccountDeletionPreview')
        this.confirmOpen = true
      } catch (error) {
        this.notify('No pudimos revisar tu cuenta', error?.response?.data?.messages?.[0] || 'Inténtalo de nuevo.', 'danger')
      } finally {
        this.loadingPreview = false
      }
    },

    resetConfirmation() {
      this.typedEmail = ''
      this.errorMessage = ''
    },

    async confirmDeletion() {
      if (!this.canConfirm) return
      this.deleting = true
      this.errorMessage = ''
      try {
        await this.$store.dispatch('users/deleteUser', this.currentUser.id)
        this.confirmOpen = false
        this.endSession()
        this.notify('Tu cuenta se eliminó', 'Gracias por haber usado YoConstruyo.', 'success')
      } catch (error) {
        this.errorMessage = error?.response?.data?.messages?.[0] || 'No pudimos eliminar tu cuenta; inténtalo de nuevo.'
      } finally {
        this.deleting = false
      }
    },

    endSession() {
      document.documentElement.style.setProperty('--primary-custom', '#340668')
      document.documentElement.style.setProperty('--secondary-custom', '#340668')
      this.$store.commit('auth/clearCurrentUser')
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)
      localStorage.removeItem('userData')
      this.$router.replace({ name: 'auth-login' })
    },

    notify(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertTriangleIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.account-deletion {
  border: 1px solid rgba(234, 84, 85, 0.35);
}
</style>
